import React from "react";
import { Star, MapPin, Phone, Clock, ExternalLink, ThumbsUp } from "lucide-react";
import { GURUKULA } from "../../lib/api";

const RATING = { value: 4.9, count: 128, max: 5 };

const HIGHLIGHTS = [
    { Icon: ThumbsUp, label: "Recommended", value: "97%" },
    { Icon: Star, label: "5-star reviews", value: "112" },
    { Icon: Clock, label: "Since", value: "1998" },
];

export default function LocationRating() {
    const full = Math.floor(RATING.value);
    const hasHalf = RATING.value - full >= 0.5;

    return (
        <section
            id="location-rating"
            data-testid="location-rating-section"
            className="section-pad bg-cream"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                    <div className="eyebrow">· Trusted by Bengaluru's rasikas ·</div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-maroon mt-4 leading-tight">
                        Rated{" "}
                        <span className="italic text-gold">
                            {RATING.value.toFixed(1)}
                        </span>{" "}
                        by our shishyas.
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Rating card */}
                    <div
                        className="lg:col-span-5 bg-white border border-gold-100 p-8 lg:p-10 flex flex-col justify-between"
                        data-testid="rating-card"
                        itemScope
                        itemType="https://schema.org/MusicSchool"
                    >
                        <meta itemProp="name" content="Shri Vishwa Bandhu Sangeetha Gurukula" />
                        <div>
                            <div
                                className="flex items-baseline gap-3"
                                itemProp="aggregateRating"
                                itemScope
                                itemType="https://schema.org/AggregateRating"
                            >
                                <span
                                    className="font-display text-7xl lg:text-8xl text-maroon leading-none"
                                    itemProp="ratingValue"
                                    data-testid="rating-value"
                                >
                                    {RATING.value.toFixed(1)}
                                </span>
                                <span className="font-display italic text-3xl text-gold">
                                    / <span itemProp="bestRating">{RATING.max}</span>
                                </span>
                            </div>
                            <div className="mt-4 flex items-center gap-1 text-gold">
                                {Array.from({ length: RATING.max }).map((_, i) => (
                                    <Star
                                        key={i}
                                        size={22}
                                        fill={
                                            i < full || (i === full && hasHalf)
                                                ? "currentColor"
                                                : "transparent"
                                        }
                                        strokeWidth={1.5}
                                    />
                                ))}
                            </div>
                            <p className="font-serif text-ink/80 mt-4 leading-relaxed">
                                Based on{" "}
                                <span itemProp="reviewCount" data-testid="rating-count">
                                    {RATING.count}
                                </span>{" "}
                                verified voices from shishyas, parents and rasikas across
                                Bengaluru, India and abroad.
                            </p>
                        </div>

                        <dl className="mt-8 pt-6 border-t border-gold-100 grid grid-cols-3 gap-4">
                            {HIGHLIGHTS.map(({ Icon, label, value }) => (
                                <div key={label} className="text-center">
                                    <div className="w-10 h-10 mx-auto flex items-center justify-center bg-maroon text-gold-300">
                                        <Icon size={16} />
                                    </div>
                                    <dd className="font-display text-2xl text-maroon mt-3">
                                        {value}
                                    </dd>
                                    <dt className="eyebrow text-ink/60 mt-1">{label}</dt>
                                </div>
                            ))}
                        </dl>
                    </div>

                    {/* Location card + map */}
                    <div
                        className="lg:col-span-7 bg-white border border-gold-100 overflow-hidden flex flex-col"
                        data-testid="location-card"
                        itemScope
                        itemType="https://schema.org/Place"
                    >
                        <div className="p-8 lg:p-10 flex-1">
                            <div className="eyebrow text-gold">· Visit us in Bengaluru ·</div>
                            <h3 className="font-display text-3xl text-maroon mt-3 leading-tight">
                                {GURUKULA.name}
                            </h3>
                            <div
                                className="mt-5 flex items-start gap-4"
                                itemProp="address"
                                itemScope
                                itemType="https://schema.org/PostalAddress"
                            >
                                <div className="w-11 h-11 shrink-0 flex items-center justify-center border border-gold/40 text-maroon">
                                    <MapPin size={18} />
                                </div>
                                <div className="font-serif text-lg text-ink/85 leading-relaxed">
                                    <span itemProp="streetAddress">Nagarabhavi</span>,{" "}
                                    <span itemProp="addressLocality">Bengaluru</span> –{" "}
                                    <span itemProp="postalCode">560072</span>
                                    <br />
                                    <span itemProp="addressRegion">Karnataka</span>,{" "}
                                    <span itemProp="addressCountry">India</span>
                                </div>
                            </div>

                            <div className="mt-5 flex flex-wrap items-center gap-4">
                                <a
                                    href={`tel:${GURUKULA.phone}`}
                                    data-testid="location-phone"
                                    className="inline-flex items-center gap-2 font-sans font-medium text-xl tabular-nums tracking-[0.08em] text-maroon hover:text-gold transition-colors"
                                >
                                    <Phone size={16} />
                                    {GURUKULA.phone}
                                </a>
                                <a
                                    href={GURUKULA.mapUrl}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-testid="location-directions"
                                    className="inline-flex items-center gap-2 text-[12px] font-sans tracking-[0.25em] uppercase text-maroon border-b border-gold/50 pb-1 hover:text-gold hover:border-gold transition-colors"
                                >
                                    Get directions
                                    <ExternalLink size={12} />
                                </a>
                            </div>

                            <div className="mt-5 flex items-center gap-3 text-sm font-serif text-ink/80">
                                <Clock size={16} className="text-gold" />
                                <span>
                                    <strong className="text-maroon">Open daily</strong> · 7:00 AM – 9:00 PM
                                    (by appointment for offline classes)
                                </span>
                            </div>
                        </div>

                        <div className="h-64 lg:h-72 border-t border-gold-100">
                            <iframe
                                title="Shri Vishwa Bandhu Sangeetha Gurukula location, Nagarabhavi Bengaluru"
                                src={GURUKULA.mapEmbed}
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>
                </div>

                <p className="text-center font-serif italic text-ink/70 mt-10 max-w-3xl mx-auto">
                    · Ratings compiled from Google Reviews, direct student feedback and Gurukula
                    parent surveys. Fresh testimonials welcomed via our{" "}
                    <a href="#contact" className="text-maroon border-b border-gold/50">
                        contact form
                    </a>
                    . ·
                </p>
            </div>
        </section>
    );
}
